import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

// ----------------------------------------------------------------------

ImageUploaderWithPreview.propTypes = {
  name: PropTypes.string.isRequired,
  currentUrl: PropTypes.string,
  accept: PropTypes.string,
  aspectRatio: PropTypes.string,
};

ImageUploaderWithPreview.defaultProps = {
  currentUrl: '',
  accept: 'image/*',
  aspectRatio: '16/9',
};

export default function ImageUploaderWithPreview({ name, currentUrl, accept, aspectRatio }) {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState(currentUrl || '');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (currentUrl) setPreviewUrl(currentUrl);
  }, [currentUrl]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const handleFile = (file) => {
          const reader = new FileReader();
          reader.onload = (ev) => setPreviewUrl(ev.target.result);
          reader.readAsDataURL(file);
          field.onChange(file);
        };

        const handleDrop = (e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFile(file);
        };

        let borderColor = 'divider';
        if (error) borderColor = 'error.main';
        else if (isDragging) borderColor = 'primary.main';
        else if (previewUrl) borderColor = 'transparent';

        let bgColor = '#f5f7ff';
        if (isDragging) bgColor = 'rgba(25, 118, 210, 0.08)';
        else if (previewUrl) bgColor = '#000';

        const iconColor = isDragging ? 'primary.main' : 'text.disabled';
        const labelColor = isDragging ? 'primary.main' : 'text.secondary';
        const dropLabel = isDragging ? t('drop_here') : t('drag_or_click_to_upload');

        return (
          <Box>
            {/* Drop zone / preview */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio,
                maxHeight: 220,
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px dashed',
                borderColor,
                background: bgColor,
                cursor: 'pointer',
                transition: 'border-color 0.2s, background 0.2s',
                '&:hover': { borderColor: previewUrl ? 'transparent' : 'primary.main' },
                '&:hover .img-overlay': { opacity: 1 },
              }}
              onClick={() => {
                if (previewUrl) setLightboxOpen(true);
                else inputRef.current?.click();
              }}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={handleDrop}
            >
              {previewUrl ? (
                <>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="preview"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {/* Hover overlay: zoom + change button */}
                  <Box
                    className="img-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.45)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <ZoomInIcon sx={{ color: '#fff', fontSize: 42 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                      sx={{
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.7)',
                        '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      {t('change_image')}
                    </Button>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    gap: 1,
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 44, color: iconColor, transition: 'color 0.2s' }} />
                  <Typography variant="body2" color={labelColor} sx={{ transition: 'color 0.2s' }}>
                    {dropLabel}
                  </Typography>
                </Box>
              )}

              {/* Hidden file input */}
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = '';
                }}
                hidden
              />
            </Box>

            {/* Validation error */}
            {error && (
              <Typography variant="caption" color="error.main" display="block" mt={0.5}>
                {error.message}
              </Typography>
            )}

            {/* Lightbox */}
            <Dialog
              open={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              maxWidth="lg"
              onClick={() => setLightboxOpen(false)}
              PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
            >
              <Box sx={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <IconButton
                  onClick={() => setLightboxOpen(false)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -16,
                    right: -16,
                    color: '#fff',
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 1,
                    '&:hover': { background: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Imagen ampliada"
                  sx={{
                    maxWidth: '90vw',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                    borderRadius: 2,
                    display: 'block',
                  }}
                />
              </Box>
            </Dialog>
          </Box>
        );
      }}
    />
  );
}
